import { useMergeRefs } from '@dni-react-hooks/use-merge-refs'
import { useOnClickAway } from '@dni-react-hooks/use-on-click-away'
import * as stylex from '@stylexjs/stylex'
import { type Dispatch, type ForwardedRef, type SetStateAction, useRef } from 'react'
import { Paper, type PaperProps } from '../Paper/Paper'
import { Popper, type PopperProps } from '../Popper/Popper'
import { zIndex } from '../tokens/zIndex.stylex'

/**
* 高度設定
* 每個選項37px, 6個選項的高度為222px; padding-top: 8px; 選項中間的間距為4px, 6個總共24px; 總共高度為254px
*/
const baseStyles = stylex.create({
  root: {
    zIndex: zIndex.modal,
  },
  paper: {
    overflow: 'auto',
    maxHeight: 254,
  },
})

export interface MenuProps extends PopperProps {
  /** 關閉選單時的回呼（點擊外部區域觸發） */
  onClose?: () => void
  /** 自訂根元素（Popper）的 StyleX 樣式 */
  rootStyle?: stylex.StyleXStyles
  /** 自訂 Paper 容器的 StyleX 樣式 */
  paperStyle?: stylex.StyleXStyles
  /** 傳遞給內部 Paper 元件的額外 props */
  PaperProps?: Omit<PaperProps, 'rootRef' | 'rootStyle'>
  /** Paper 元素的 ref */
  rootRef?: ForwardedRef<HTMLDivElement> | Dispatch<SetStateAction<HTMLDivElement | null>>
}

/**
 * Menu 是下拉選單元件，基於 Popper 定位並使用 Paper 作為容器。
 * 點擊外部區域時會自動觸發 onClose。
 */
export const Menu = (props: MenuProps) => {
  const {
    children,
    open,
    anchorEl,
    onClose,
    rootStyle,
    paperStyle,
    PaperProps,
    rootRef: rootRefProp,
    ...rest
  } = props
  const paperRef = useRef<HTMLDivElement>(null)

  const mergeRef = useMergeRefs(rootRefProp, paperRef)

  useOnClickAway({
    ref: paperRef,
    callback: onClose,
  })

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom"
      {...stylex.props(baseStyles.root, rootStyle)}
      {...rest}
    >
      <Paper
        rootRef={mergeRef}
        rootStyle={[baseStyles.paper, paperStyle]}
        {...PaperProps}
      >
        {children}
      </Paper>
    </Popper>
  )
}
